import React, { memo, useEffect, useState } from 'react';
import { Box, Button, Chip, IconButton, Stack, TextField, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ConfirmDialog from '../../../shared/components/ConfirmDialog';
import InlineAlert from '../../../shared/components/InlineAlert';
import WorkflowDiagram from '../../workflow/components/WorkflowDiagram';
import { WORKFLOW_TOOLS } from '../../toolset/catalog';
import { workflowToolOptions } from '../utils/workflowToolOptions';
import { nodesOf, resultsAvailableTo } from '../utils/workflowDraft';
import WorkflowStepEditor from './WorkflowStepEditor';
import WorkflowPublishButton from './WorkflowPublishButton';
import { useWorkflowDraft } from '../hooks/useWorkflowDraft';
import type { WorkflowDraftSubmission } from '../hooks/useWorkflowForm';
import type { Workflow } from '../../../types/backend';

// Free entry stays open: the backend refuses an unknown name at publish and says which.
const WRITE_TOOL_NAMES = workflowToolOptions(WORKFLOW_TOOLS);

interface Props {
  /** The stored draft, or null for a workflow that does not exist yet. */
  workflow: Workflow | null;
  saving: boolean;
  publishing: boolean;
  actionError: string | null;
  notice: string | null;
  onDismissError: () => void;
  onSave: (draft: WorkflowDraftSubmission) => Promise<boolean>;
  onPublish: () => Promise<boolean>;
  onCancel: () => void;
  /** Reports whether leaving now would lose something, so the Studio can ask before it does. */
  onDirtyChange: (dirty: boolean) => void;
  /** An exit the Studio is holding until the operator answers. */
  discardPending: boolean;
  onConfirmDiscard: () => void;
  onKeepEditing: () => void;
}

// Edits a draft in the same stages and step cards the viewer shows, so what an operator builds
// is what they will later see a run walk.
const WorkflowEditor = memo<Props>(({
  workflow,
  saving,
  publishing,
  actionError,
  notice,
  onDismissError,
  onSave,
  onPublish,
  onCancel,
  onDirtyChange,
  discardPending,
  onConfirmDiscard,
  onKeepEditing,
}) => {
  const draft = useWorkflowDraft(workflow);
  // The step being configured, by the draft's own key. Neither a position nor an id can do this
  // job: a position moves whenever a stage is added, moved or split, and an id follows the step's
  // name as the operator types it, so both lose the form mid-edit.
  const [openKey, setOpenKey] = useState<string | null>(
    workflow === null ? draft.stages[0]?.[0]?.key ?? null : null,
  );
  // A step is added by a dispatch, so it exists on the next render: this remembers which stage
  // to open the newcomer in. Without it, adding a step left the operator looking at the empty
  // state next to a step they had just asked for.
  const [addedTo, setAddedTo] = useState<number | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const isNew = workflow === null;
  // The working copy lives here and dies with this component, so the Studio is told what leaving
  // would cost. Reported rather than asked for, because only the editor knows.
  useEffect(() => onDirtyChange(draft.hasChanges), [draft.hasChanges, onDirtyChange]);
  // And nothing is left to lose once this copy is gone. Without it the Studio would hold the last
  // answer of an editor that is no longer on screen, and go on asking a question whose dialog
  // this component draws.
  useEffect(() => () => onDirtyChange(false), [onDirtyChange]);
  useEffect(() => {
    if (addedTo === null) return;
    const members = draft.stages[addedTo];
    if (members && members.length > 0) setOpenKey(members[members.length - 1].key);
    setAddedTo(null);
  }, [addedTo, draft.stages]);
  // The open form is keyed by position, so any change that moves steps closes it rather than
  // letting it land on whichever step now sits there.
  const locate = (key: string | null) => {
    if (key === null) return null;
    for (const [si, members] of draft.stages.entries()) {
      const ni = members.findIndex((n) => n.key === key);
      if (ni !== -1) return { si, ni, node: members[ni] };
    }
    return null;
  };
  const selected = locate(openKey);
  // A step the operator removed is not a step the form should find its way back to.
  const forget = (change: () => void) => {
    setOpenKey(null);
    change();
  };

  const handleSave = () => {
    const submission = draft.submission();
    if (submission) void onSave(submission);
  };

  return (
    <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', p: 2.5, gap: 1.5 }}>
      {/* The name of the thing, editable where it is shown. Two full-width labelled fields
          stacked above the canvas pushed the workflow itself off the screen, which is backwards:
          the shape is the work, the title is a detail of it. */}
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexWrap: 'wrap' }}>
        <TextField
          variant="standard"
          label="Title"
          value={draft.title}
          onChange={(e) => draft.setTitle(e.target.value)}
          sx={{ flex: '1 1 260px', minWidth: 200, '& input': { fontSize: '1.15rem', fontWeight: 500 } }}
        />
        {isNew && (
          <TextField
            variant="standard"
            label="Name"
            value={draft.name}
            onChange={(e) => draft.setName(e.target.value)}
            helperText="snake_case, fixed once created"
            sx={{ flex: '0 1 220px' }}
          />
        )}
        <Chip size="small" label="Draft" />
        <Button size="small" onClick={() => setShowDetails((open) => !open)}>
          {showDetails ? 'Hide description' : 'Description'}
        </Button>
      </Stack>

      {showDetails && (
        <TextField
          size="small"
          label="Description"
          multiline
          minRows={2}
          value={draft.description}
          onChange={(e) => draft.setDescription(e.target.value)}
        />
      )}

      <Stack spacing={1}>
        {actionError && (
          <InlineAlert severity="error" onClose={onDismissError}>
            {actionError}
          </InlineAlert>
        )}
        {draft.refusal && (
          <InlineAlert severity="warning" onClose={draft.dismissRefusal}>
            {draft.refusal}
          </InlineAlert>
        )}
        {draft.unreachable.map((warning) => (
          <InlineAlert key={warning} severity="warning">
            {warning}
          </InlineAlert>
        ))}
        {draft.errors.length > 0 && (
          <InlineAlert severity="error">
            <Box component="ul" sx={{ m: 0, pl: 2 }}>
              {draft.errors.map((e) => (
                <li key={e}>{e}</li>
              ))}
            </Box>
          </InlineAlert>
        )}
        {notice && !actionError && <InlineAlert severity="success">{notice}</InlineAlert>}
      </Stack>

      {/* The drawing is the editor's structure surface, not an illustration beside one. An
          author builds a workflow by looking at its shape and clicking the part they mean; the
          form on the right configures whatever that is. Before this the shape was a picture above
          a long list of cards, and the list was where the work happened, so the routes were
          authored by people who never looked at them whole. */}
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'stretch', flex: 1, minHeight: 0 }}>
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            border: 1,
            borderColor: 'divider',
            borderRadius: 2,
            bgcolor: 'background.default',
            overflow: 'hidden',
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ px: 1.5, py: 1, borderBottom: 1, borderColor: 'divider' }}
          >
            <Typography variant="caption" color="text.secondary">
              The whole workflow. Click a step to configure it.
            </Typography>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => {
                draft.addStage();
                setOpenKey(null);
              }}
            >
              Add stage
            </Button>
          </Stack>
          <Box sx={{ flex: 1, minHeight: 280, overflow: 'auto', px: 1.5 }}>
            <WorkflowDiagram
              nodes={nodesOf(draft.stages)}
              selectedId={selected?.node.id}
              onSelectStep={(nodeId) => {
                const found = draft.stages.flat().find((n) => n.id === nodeId);
                if (found) setOpenKey(found.key);
              }}
            />
          </Box>
        </Box>

        {/* The settings only take width while something is being configured. An empty pane
            holding half the screen was the reason the drawing had none. */}
        <Box
          role="region"
          aria-label="Step settings"
          sx={{
            flex: '0 0 auto',
            width: selected === null ? 200 : 430,
            border: 1,
            borderColor: 'divider',
            borderRadius: 2,
            p: 1.5,
            overflow: 'auto',
          }}
        >
          {selected === null ? (
            <Stack spacing={1} alignItems="flex-start">
              <Typography variant="body2" color="text.secondary">
                {draft.stages.every((members) => members.length === 0)
                  ? 'This workflow has no steps yet.'
                  : 'Pick a step to configure it.'}
              </Typography>
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() => {
                  const si = Math.max(0, draft.stages.length - 1);
                  setAddedTo(si);
                  draft.addStep(si);
                }}
              >
                Add step
              </Button>
            </Stack>
          ) : (
            <>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ mb: 1 }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle2" noWrap>
                    {selected.node.label || 'Untitled step'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Stage {selected.si + 1}
                  </Typography>
                </Box>
                <Stack direction="row">
                  <IconButton
                    size="small"
                    aria-label={`Move stage ${selected.si + 1} up`}
                    disabled={selected.si === 0}
                    onClick={() => draft.moveStage(selected.si, selected.si - 1)}
                  >
                    <ArrowUpwardIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    aria-label={`Move stage ${selected.si + 1} down`}
                    disabled={selected.si === draft.stages.length - 1}
                    onClick={() => draft.moveStage(selected.si, selected.si + 1)}
                  >
                    <ArrowDownwardIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    aria-label={`Remove ${selected.node.label || 'untitled step'}`}
                    onClick={() => forget(() => draft.removeStep(selected.si, selected.ni))}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Stack>
              </Stack>

              <WorkflowStepEditor
                node={selected.node}
                stageIndex={selected.si}
                stageCount={draft.stages.length}
                siblings={draft.stages[selected.si].filter((_, i) => i !== selected.ni)}
                availableResults={resultsAvailableTo(draft.stages, selected.si, selected.ni)}
                toolOptions={WRITE_TOOL_NAMES}
                onChange={(patch) => draft.updateStep(selected.si, selected.ni, patch)}
                onAddRoute={() => draft.addRoute(selected.si, selected.ni)}
                onRouteChange={(ri, patch) => draft.updateRoute(selected.si, selected.ni, ri, patch)}
                onRemoveRoute={(ri) => draft.removeRoute(selected.si, selected.ni, ri)}
              />

              <Stack
                direction="row"
                spacing={1}
                sx={{ mt: 2, pt: 1.5, borderTop: 1, borderColor: 'divider' }}
              >
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  aria-label={`Add step to stage ${selected.si + 1}`}
                  onClick={() => {
                    setAddedTo(selected.si);
                    draft.addStep(selected.si);
                  }}
                >
                  Add step
                </Button>
                <Button
                  size="small"
                  color="inherit"
                  aria-label={`Remove stage ${selected.si + 1}`}
                  onClick={() => forget(() => draft.removeStage(selected.si))}
                >
                  Remove stage
                </Button>
              </Stack>
            </>
          )}
        </Box>
      </Box>

      <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
        <Button onClick={onCancel} color="inherit">
          {draft.hasChanges ? 'Discard changes' : isNew ? 'Discard' : 'Done editing'}
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={saving || publishing || (!isNew && !draft.hasChanges)}>
          {saving ? 'Saving...' : isNew ? 'Create draft' : 'Save draft'}
        </Button>
        {!isNew && (
          <WorkflowPublishButton
            publishing={publishing}
            disabled={saving || draft.hasChanges}
            disabledReason="Save the draft before publishing it"
            onPublish={onPublish}
          />
        )}
      </Stack>

      <ConfirmDialog
        open={discardPending}
        title="Discard unsaved changes?"
        confirmText="Discard changes"
        confirmColor="warning"
        cancelText="Keep editing"
        onCancel={onKeepEditing}
        onConfirm={onConfirmDiscard}
      >
        The stages you have edited have not been saved. Leaving now loses them.
      </ConfirmDialog>
    </Box>
  );
});

WorkflowEditor.displayName = 'WorkflowEditor';

export default WorkflowEditor;
